import React, { useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStop } from '@fortawesome/free-solid-svg-icons';
import { faUserSecret, faGraduationCap, faPortalEnter } from '@fortawesome/pro-light-svg-icons';
import { stopImpersonating } from '../../store/impersonationSlice';
import { clearCourse } from '../../store/courseSlice';

function ImpersonationBanner({ onSwitchUser, onSwitchCourse }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isImpersonating = useSelector((state) => state.impersonation.isImpersonating);
  const user = useSelector((state) => state.impersonation.impersonation);
  const courseId = useSelector((state) => state.course.courseId);

  const bannerRef = useRef(null);

  // The banner is position:fixed, so content below it uses a CSS variable
  // --banner-height for its margin-top offset. A ResizeObserver keeps the
  // variable in sync with the banner's actual rendered height, which changes
  // when buttons wrap on narrow viewports or when impersonation/course
  // toggles add or remove rows.
  useEffect(() => {
    if (!bannerRef.current) return;
    const updateOffset = () => {
      const height = bannerRef.current.offsetHeight;
      document.documentElement.style.setProperty('--banner-height', `${height}px`);
    };
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(bannerRef.current);
    return () => observer.disconnect();
  }, [isImpersonating, courseId]);

  if (!isImpersonating && !courseId) return null;

  return (
    <Box
      ref={bannerRef}
      className="mss__impersonationBanner"
      role="banner"
      aria-label="Impersonation Banner"
      title="Impersonation Banner"
    >
      <Box className="bannerContent">
        <Box className="bannerInfo">
          {isImpersonating && user && (
            <>
              <FontAwesomeIcon
                icon={faUserSecret}
                size="lg"
                style={{ paddingRight: '1rem', display: 'inline' }}
              />
              Impersonating
              <Typography sx={{ fontWeight: 'bold', display: 'inline' }}>
                &nbsp;{user.midas}
              </Typography>
              {courseId && <>&nbsp;|&nbsp;</>}
            </>
          )}
          {courseId && (
            <>
              Course:&nbsp;
              <Typography sx={{ fontWeight: 'bold', display: 'inline' }}>{courseId}</Typography>
            </>
          )}
        </Box>

        <Box className="bannerActions">
          {isImpersonating && (
            <>
              <Button
                variant="text"
                startIcon={<FontAwesomeIcon icon={faStop} />}
                onClick={() => dispatch(stopImpersonating())}
              >
                Revert to Self
              </Button>
              <Button
                variant="text"
                startIcon={<FontAwesomeIcon icon={faPortalEnter} />}
                onClick={onSwitchUser}
              >
                Switch User
              </Button>
            </>
          )}
          <Button
            variant="text"
            startIcon={<FontAwesomeIcon icon={faGraduationCap} />}
            onClick={onSwitchCourse}
          >
            {courseId ? 'Switch Course' : 'Set Course'}
          </Button>
          {courseId && (
            <Button
              variant="text"
              startIcon={<FontAwesomeIcon icon={faStop} />}
              onClick={() => {
                dispatch(clearCourse());
                navigate('/');
              }}
            >
              Clear Course
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default ImpersonationBanner;
